'use client'

import Link from 'next/link'
import Image from 'next/image'

interface CTASectionProps {
  heading: string
  subheading?: string
  buttonText: string
  buttonLink: string
  secondaryButtonText?: string
  secondaryButtonLink?: string
  backgroundVariant?: 'default' | 'gold' | 'blue' | 'dark' | 'gradient'
  className?: string
  backgroundImage?: string
  centerLogo?: boolean
}

export default function CTASection({
  heading,
  subheading,
  buttonText,
  buttonLink,
  secondaryButtonText,
  secondaryButtonLink,
  backgroundVariant = 'default',
  className = '',
  backgroundImage,
  centerLogo = false
}: CTASectionProps) {
  const backgroundClasses = {
    default: 'bg-slate-950',
    gold: 'bg-gradient-to-br from-eskerium-gold/90 to-eskerium-gold',
    blue: 'bg-gradient-to-br from-eskerium-blue/90 to-eskerium-blue',
    dark: 'bg-gradient-to-br from-eskerium-navy to-eskerium-dark',
    gradient: 'bg-gradient-to-br from-eskerium-blue via-eskerium-navy to-eskerium-gold'
  }

  const textColor = 'text-white'
  const subtextColor = 'text-white/80'

  return (
    <section className={`relative py-20 md:py-28 overflow-hidden ${backgroundClasses[backgroundVariant]} ${className}`}>
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0">
        {backgroundImage ? (
          <Image
            src={backgroundImage}
            alt=""
            fill
            className="object-cover opacity-30"
          />
        ) : (
          <>
            {/* Aurora gradient background */}
            <div className="absolute inset-0 bg-aurora opacity-20"></div>
            {/* Grid pattern overlay */}
            <div className="absolute inset-0 bg-grid-pattern bg-grid-pattern opacity-10"></div>
          </>
        )}
        
        {/* Enhanced overlay with gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30"></div>
        
        {/* Floating accent elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-eskerium-blue/10 to-eskerium-gold/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-r from-eskerium-gold/10 to-eskerium-blue/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 section-container">
        <div className="text-center">
          {/* Logo (if centerLogo is true) */}
          {centerLogo && (
            <div className="mb-12 animate-fade-in">
              <Image
                src="/images/eskerium-wordmark-white.png"
                alt="Eskerium"
                width={264}
                height={80}
                className="mx-auto h-auto transition-transform duration-300 hover:scale-105"
                priority
              />
            </div>
          )}

          {/* Enhanced Heading with Gradient Text */}
          <h2 className={`heading-lg mb-8 ${textColor} max-w-4xl mx-auto leading-tight animate-fade-in`}>
            <span className="bg-gradient-to-r from-white via-white to-slate-200 bg-clip-text text-transparent">
              {heading.split(' ').slice(0, -2).join(' ')}
            </span>{' '}
            <span className="bg-gradient-to-r from-eskerium-gold via-yellow-300 to-eskerium-gold bg-clip-text text-transparent italic">
              {heading.split(' ').slice(-2).join(' ')}
            </span>
          </h2>

          {/* Subheading */}
          {subheading && (
            <p className={`text-xl md:text-2xl mb-12 ${subtextColor} max-w-3xl mx-auto leading-relaxed animate-fade-in-delayed`}>
              {subheading}
            </p>
          )}

          {/* Enhanced CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8 animate-fade-in-delayed">
            {/* Primary CTA - Pill-shaped with gradient */}
            <Link
              href={buttonLink}
              className={`group relative inline-flex items-center px-8 py-4 text-lg font-medium rounded-full transition-all duration-300 ease-gentle hover:scale-105 hover:-translate-y-1 hover:shadow-elev-2 overflow-hidden
                ${['gold'].includes(backgroundVariant)
                  ? 'bg-gradient-to-r from-eskerium-navy to-eskerium-dark text-white hover:from-eskerium-dark hover:to-eskerium-navy'
                  : 'bg-gradient-to-r from-eskerium-gold to-eskerium-gold/90 text-eskerium-dark hover:from-eskerium-gold/90 hover:to-eskerium-gold'
                }`}
            >
              <span className="relative z-10 flex items-center gap-2">
                {buttonText}
                <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </Link>

            {/* Secondary CTA - Glass effect */}
            {secondaryButtonText && secondaryButtonLink && (
              <Link
                href={secondaryButtonLink}
                className={`group card-glass border border-white/20 hover:border-white/40 text-white hover:bg-white/10 inline-flex items-center px-8 py-4 text-lg font-medium rounded-full transition-all duration-300 ease-gentle hover:scale-105 hover:-translate-y-1`}
              >
                <span className="flex items-center gap-2">
                  {secondaryButtonText}
                  <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </Link>
            )}
          </div>

          {/* Additional content for gold variant */}
          {backgroundVariant === 'gold' && (
            <div className="mt-16 max-w-2xl mx-auto animate-fade-in-delayed">
              <div className="card-glass p-8 rounded-2xl">
                <p className={`text-lg ${subtextColor} leading-relaxed`}>
                  Our mission is to bring true wellness to people&apos;s lives by providing brands with the highest-quality hemp ingredients. We believe in the power of transparency, quality, and collaboration to create products that make a real difference.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Subtle animated border at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-eskerium-gold/50 to-transparent"></div>
    </section>
  )
}